export interface Metric {
    label: string;
    value: string;
    icon: 'trend' | 'star' | 'time' | 'money' | 'people' | 'check';
    color?: string;
}

export interface CaseStudyContent {
    title: string;
    descriptions: string[];
}

export interface CaseStudyProps {
    title: string;
    subtitle: string;
    chipLabel: string;
    contents: CaseStudyContent[];
    metrics: Metric[];
    slug: string;
    image: string;
}

export const caseStudiesData: CaseStudyProps[] = [
    {
        title: "Case 01｜Airbnb専用プラン",
        subtitle: "対象：個人オーナーAirbnb単独運営",
        chipLabel: "34 部屋",
        slug: "airbnb-dedicated-plan",
        image: "/images/case-studies.png",
        contents: [
            {
                title: "「まずはAirbnbのメッセージ代行だけ。でも成果は最大化。」",
                descriptions: []
            },
            {
                title: "背景",
                descriptions: [
                    "Airbnbのみで物件を運営していたが、日々のゲスト対応・価格調整・レビュー管理に時間を取られ、運営の負担と収益の安定化が課題となっていた。",
                ]
            },
            {
                title: "AirXpress 導入内容",
                descriptions: [
                    "Airbnb運用に特化した管理・改善。",
                    "ゲスト対応（メール／チャット）の標準化。",
                    "レビュー品質を維持する運営フロー設計。"
                ]
            },
            {
                title: "導入後の変化",
                descriptions: [
                    "複雑な運営を増やすことなく、Airbnb単独でも安定した稼働と高評価を維持。シンプルなAirbnbメール代行のため、個人オーナーでも無理なく物件を増やせるモデルを実現。",
                ]
            },
        ],
        metrics: [
            { label: "初月稼働率", value: "57.69%", icon: "trend", color: "#F15859" },
            { label: "平均レビュー", value: "4.78/5", icon: "star", color: "#F15859" },
            { label: "問い合わせ→予約率", value: "91%", icon: "time", color: "#F15859" }, // Dark blue as per image
            { label: "月次売上", value: "26,380,000円", icon: "money", color: "#F15859" },
        ]
    }, {
        title: "Case 02｜複数OTAプラン",
        subtitle: "対象：法人宿泊事業者複数OTA運営",
        chipLabel: "13 部屋",
        slug: "multiple-ota-plan",
        image: "/images/case-studies.png",
        contents: [
            {
                title: "「チャネルが増えても、管理はシンプルに。」",
                descriptions: []
            },
            {
                title: "背景",
                descriptions: [
                    "Airbnb・Booking.com など複数OTAを活用して集客していたが、ゲスト対応・予約管理・在庫調整・料金更新が煩雑になり、運営負担の増加と人的コストが課題となっていた。",
                ]
            },
            {
                title: "AirXpress 導入内容",
                descriptions: [
                    "複数OTAの予約・在庫の一元管理。",
                    "OTA間の在庫・料金調整の最適化。",
                    "ゲスト対応フローの統一（メール／チャット）",
                    "日々の運営オペレーションの標準化。"
                ]
            },
            {
                title: "導入後の変化",
                descriptions: [
                    "複数チャネルを使いながらも、安定したゲスト対応体制を構築。",
                    "中規模運営でも、少ない人員で継続可能な集客モデルを実現。",
                ]
            },
        ],
        metrics: [
            { label: "初月稼働率", value: "50.6%", icon: "trend", color: "#F15859" },
            { label: "平均レビュー", value: "4.0/5", icon: "star", color: "#F15859" },
            { label: "問い合わせ→予約率", value: "78.74%", icon: "time", color: "#F15859" }, // Dark blue as per image
            { label: "月次売上", value: "2,760,000円", icon: "money", color: "#F15859" },
        ]
    }, {
        title: "Case 03｜Web運用プラン",
        subtitle: "対象：法人運営者直販強化",
        chipLabel: "14 部屋",
        slug: "web-operation-plan",
        image: "/images/case-studies.png",
        contents: [
            {
                title: "「オンライン完結で、高稼働率を実現。」",
                descriptions: []
            },
            {
                title: "背景",
                descriptions: [
                    "OTA中心の運営では、手数料負担や運営効率に限界を感じていた。",
                    "現地対応は不要だが、予約管理・料金調整・ゲスト対応をオンラインで効率化し、安定した稼働を実現したいという課題があった。",
                ]
            },
            {
                title: "AirXpress 導入内容",
                descriptions: [
                    "オンライン完結型の運営管理システムを導入。",
                    "予約管理・料金調整・在庫管理の最適化。",
                    "• ゲスト対応はメール・チャットを中心に、運営フローを標準化。",
                    "現地対応を伴わないことで、運営コストを抑制。"
                ]
            },
            {
                title: "導入後の変化",
                descriptions: [
                    "オンライン業務に集中することで、高稼働率を維持。",
                    "OTA依存を抑え、利益率の高い運営モデルへと転換。",
                ]
            },
        ],
        metrics: [
            { label: "初月稼働率", value: "83.9%", icon: "trend", color: "#F15859" },
            { label: "平均レビュー", value: "4.67/5", icon: "star", color: "#F15859" },
            { label: "問い合わせ→予約率", value: "100%", icon: "time", color: "#F15859" }, // Dark blue as per image
            { label: "月次売上", value: "5,450,000円", icon: "money", color: "#F15859" },
        ]
    }, {
        title: "Case 04｜完全運営代行プラン",
        subtitle: "対象：小規模事業者運営リソース不足",
        chipLabel: "1 部屋",
        slug: "full-management-plan",
        image: "/images/case-studies.png",
        contents: [
            {
                title: "「現地対応まで任せて、運営ストレスゼロへ。」",
                descriptions: []
            },
            {
                title: "背景",
                descriptions: [
                    "運営に割ける人手や時間が限られており、予約管理やゲスト対応、現地対応まで自社で行うことが難しかった。安定した運営を維持しながら、日常業務の負担を最小限に抑えたいという課題があった。",
                ]
            },
            {
                title: "AirXpress 導入内容",
                descriptions: [
                    "リスティング作成とセットアップ、予約管理・料金調整・ゲスト対応を一括して代行。",
                    "清掃手配や鍵対応など、現地業務まで含めたフルサポート体制を構築。",
                    "運営フローを標準化し、品質を安定化。",
                ]
            },
            {
                title: "導入後の変化",
                descriptions: [
                    "運営を全面的に任せることで、オーナーの業務負担を大幅に軽減。",
                    "小規模でも、安定した稼働と高評価を維持できる運営体制を実現。",
                ]
            },
        ],
        metrics: [
            { label: "初月稼働率", value: "67.7%", icon: "trend", color: "#F15859" },
            { label: "平均レビュー", value: "4.6/5", icon: "star", color: "#F15859" },
            { label: "問い合わせ→予約率", value: "100%", icon: "time", color: "#F15859" },
            { label: "月次売上", value: "880,000円", icon: "money", color: "#F15859" },
        ]
    },
];
